import { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { Save } from 'lucide-react';
import { db } from '@/lib/firebase';
import { SITE, type Business } from '@/data/site';

/** Module-level field component — defined OUTSIDE the page so it keeps focus
 *  across re-renders (a component defined inside render remounts every keystroke). */
function TextField({
  label, value, onChange, placeholder, type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="field" type={type} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function Settings() {
  const [form, setForm] = useState<Business>(SITE);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'settings', 'business'));
        if (snap.exists()) setForm((f) => ({ ...f, ...(snap.data() as Partial<Business>) }));
      } catch {
        /* keep defaults */
      } finally {
        setLoaded(true);
      }
    })();
  }, []);

  const setStr = (key: keyof Business, value: string) => setForm((f) => ({ ...f, [key]: value }) as Business);

  async function save() {
    setBusy(true);
    try {
      await setDoc(doc(db, 'settings', 'business'), form, { merge: true });
      setToast('Saved. The website and emails now use these details.');
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
      setTimeout(() => setToast(''), 4000);
    }
  }

  if (!loaded) return <p className="font-body text-sm text-muted">Loading settings…</p>;

  return (
    <div className="max-w-3xl">
      <h1 className="font-display text-3xl text-espresso">Settings</h1>
      <p className="mt-1 font-body text-sm text-muted">
        The single source of truth for the website <em>and</em> the booking/receipt emails. Change it here once.
      </p>

      <div className="mt-8 space-y-8">
        <section className="border border-line bg-paper p-6">
          <p className="eyebrow mb-4">Identity</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Display name" value={form.name} onChange={(v) => setStr('name', v)} />
            <TextField label="Legal name" value={form.legalName} onChange={(v) => setStr('legalName', v)} />
            <TextField label="Tagline" value={form.tagline} onChange={(v) => setStr('tagline', v)} />
            <TextField label="City" value={form.city} onChange={(v) => setStr('city', v)} />
          </div>
        </section>

        <section className="border border-line bg-paper p-6">
          <p className="eyebrow mb-4">Contact &amp; routing</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Address" value={form.address} onChange={(v) => setStr('address', v)} />
            <TextField label="Phone (display)" value={form.phone} onChange={(v) => setStr('phone', v)} placeholder="+267 …" />
            <TextField label="Phone (dial)" value={form.phoneDial} onChange={(v) => setStr('phoneDial', v)} placeholder="+267…" />
            <TextField label="WhatsApp number" value={form.whatsapp} onChange={(v) => setStr('whatsapp', v)} placeholder="26772…" />
            <TextField label="Public contact email" value={form.contactEmail} onChange={(v) => setStr('contactEmail', v)} type="email" />
            <TextField label="Bookings inbox" value={form.bookingsEmail} onChange={(v) => setStr('bookingsEmail', v)} type="email" />
            <TextField label="Admin email" value={form.adminEmail} onChange={(v) => setStr('adminEmail', v)} type="email" />
          </div>
          <p className="mt-3 font-body text-xs text-muted">
            The mailbox that actually <em>sends</em> mail is a separate server setting. These control where notices go and what shows publicly.
          </p>
        </section>

        <section className="border border-line bg-paper p-6">
          <p className="eyebrow mb-4">Social</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Instagram URL" value={form.social.instagram} onChange={(v) => setForm((f) => ({ ...f, social: { ...f.social, instagram: v } }))} />
            <TextField label="Facebook URL" value={form.social.facebook} onChange={(v) => setForm((f) => ({ ...f, social: { ...f.social, facebook: v } }))} />
          </div>
        </section>

        <section className="border border-line bg-paper p-6">
          <p className="eyebrow mb-4">Payment details (gift cards)</p>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="FNB Pay to Cell" value={form.payment.payToCell} onChange={(v) => setForm((f) => ({ ...f, payment: { ...f.payment, payToCell: v } }))} />
            <TextField label="Orange Money" value={form.payment.orangeMoney} onChange={(v) => setForm((f) => ({ ...f, payment: { ...f.payment, orangeMoney: v } }))} />
            <TextField label="MyZaka" value={form.payment.myzaka} onChange={(v) => setForm((f) => ({ ...f, payment: { ...f.payment, myzaka: v } }))} />
            <TextField label="Bank name" value={form.payment.bankName} onChange={(v) => setForm((f) => ({ ...f, payment: { ...f.payment, bankName: v } }))} />
            <TextField label="Account name" value={form.payment.accountName} onChange={(v) => setForm((f) => ({ ...f, payment: { ...f.payment, accountName: v } }))} />
            <TextField label="Account number" value={form.payment.accountNumber} onChange={(v) => setForm((f) => ({ ...f, payment: { ...f.payment, accountNumber: v } }))} />
            <TextField label="Branch code" value={form.payment.branchCode} onChange={(v) => setForm((f) => ({ ...f, payment: { ...f.payment, branchCode: v } }))} />
          </div>
          <p className="mt-3 font-body text-xs text-muted">These appear on the gift-card payment screen. Update them to your real accounts.</p>
        </section>

        <section className="border border-line bg-paper p-6">
          <p className="eyebrow mb-4">Opening hours</p>
          <div className="space-y-3">
            {form.hours.map((h, i) => (
              <div key={i} className="grid grid-cols-2 gap-3">
                <input className="field" value={h.day}
                  onChange={(e) => setForm((f) => ({ ...f, hours: f.hours.map((x, j) => (j === i ? { ...x, day: e.target.value } : x)) }))} />
                <input className="field" value={h.time}
                  onChange={(e) => setForm((f) => ({ ...f, hours: f.hours.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)) }))} />
              </div>
            ))}
          </div>
        </section>

        <button onClick={save} disabled={busy} className="btn-primary">
          <Save size={16} /> {busy ? 'Saving…' : 'Save settings'}
        </button>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 bg-espresso px-5 py-3 font-body text-sm text-cream shadow-soft">{toast}</div>
      )}
    </div>
  );
}
